import React from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";

/**
 * Normalize markdown so:
 * - Paragraphs that follow "N. Title:" are part of the list item (blank line + 4-space indent).
 * - Bullets "- (a)" under a numbered item stay nested (4-space indent) so the list doesn't restart.
 */
function normalizeLegalMarkdown(text: string): string {
  const lines = text.split("\n");
  const out: string[] = [];
  let inNumberedItem = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const numberedItemMatch = /^\d+\.\s/.test(line);
    const bulletMatch = /^-\s/.test(line);
    const empty = /^\s*$/.test(line);
    const isHeading = /^#+\s/.test(line);
    if (numberedItemMatch) {
      inNumberedItem = true;
      out.push(line);
    } else if (bulletMatch && inNumberedItem) {
      out.push("    " + line);
    } else if (bulletMatch) {
      out.push(line);
    } else if (empty) {
      inNumberedItem = false;
      out.push(line);
    } else if (inNumberedItem && isHeading) {
      inNumberedItem = false;
      out.push(line);
    } else if (inNumberedItem) {
      out.push("", "    " + line);
      inNumberedItem = true;
    } else {
      out.push(line);
    }
  }
  return out.join("\n");
}

const legalProseClasses = {
  // Container
  wrapper: "legal-doc-content text-gray-700 text-sm max-w-none",
  // Headings (font-medium = negrita más sutil)
  h2: "text-base font-medium text-gray-900 mt-4 mb-2 first:mt-0",
  h3: "text-sm font-medium text-gray-900 mt-3 mb-1",
  // Paragraphs
  p: "my-2 leading-relaxed",
  // Ordered list: numbers, indentation, spacing between items
  ol: "list-decimal pl-6 my-3 space-y-2",
  // Unordered (including nested under ol)
  ul: "list-disc pl-6 my-2 space-y-1",
  li: "pl-1 my-1.5 [&>p]:my-1 [&>ol]:my-1 [&>ul]:my-1",
};

const components: Components = {
  h2: ({ children }) => (
    <h2 className={legalProseClasses.h2}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className={legalProseClasses.h3}>{children}</h3>
  ),
  p: ({ children }) => <p className={legalProseClasses.p}>{children}</p>,
  ol: ({ children }) => (
    <ol className={legalProseClasses.ol}>{children}</ol>
  ),
  ul: ({ children }) => (
    <ul className={legalProseClasses.ul}>{children}</ul>
  ),
  li: ({ children }) => (
    <li className={legalProseClasses.li}>{children}</li>
  ),
};

interface LegalDocumentContentProps {
  content: string;
  className?: string;
}

export function LegalDocumentContent({
  content,
  className = "",
}: LegalDocumentContentProps) {
  const normalized = normalizeLegalMarkdown(content || "");
  return (
    <div className={`${legalProseClasses.wrapper} ${className}`.trim()}>
      <ReactMarkdown components={components}>{normalized}</ReactMarkdown>
    </div>
  );
}
